import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  ClipboardCheck, CheckCircle2, XCircle, ShieldAlert, ShieldCheck,
  Search, RefreshCw, AlertTriangle, Store, User as UserIcon, Clock,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const RISK_TONE = { high: "danger", unusual: "warn", minor: "warn", normal: "mute" };
const RISK_LABEL = { high: "🔴 Risiko Tinggi", unusual: "🟠 Tidak Biasa", minor: "🟡 Variansi Kecil", normal: "🟢 Normal" };
const STATUS_LABEL = { awaiting_owner: "Menunggu Persetujuan Owner", flagged_fraud: "Ditandai: Potensi Fraud",
  pending_approval: "Waste Melebihi Ambang — Perlu Konfirmasi Owner",
  executed: "Dieksekusi", rejected: "Ditolak" };
const STATUS_TONE = { awaiting_owner: "warn", flagged_fraud: "danger", pending_approval: "warn",
  executed: "success", rejected: "mute" };

function ActionCard({ op, busy, onAct }) {
  const pv = op.preview || {};
  const isFraud = op.status === "flagged_fraud";
  const isWasteApproval = op.status === "pending_approval";
  return (
    <Card className="p-4" data-testid={`ca-op-${op.id}`}>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-heading text-sm font-bold text-ink">{pv.title || op.action}</p>
          {pv.summary && <p className="text-xs text-ink-soft">{pv.summary}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={RISK_TONE[op.risk_level] || "mute"} data-testid={`ca-risk-${op.id}`}>
            {RISK_LABEL[op.risk_level] || op.risk_level}
          </Badge>
          <Badge tone={STATUS_TONE[op.status] || "mute"} data-testid={`ca-status-${op.id}`}>
            {STATUS_LABEL[op.status] || op.status}
          </Badge>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-mute">
        <span className="flex items-center gap-1"><ClipboardCheck size={12} /> {op.action}</span>
        {op.initiator_branch_name && <span className="flex items-center gap-1"><Store size={12} /> {op.initiator_branch_name}</span>}
        <span className="flex items-center gap-1"><UserIcon size={12} /> {op.initiator_name} ({op.initiator_role})</span>
        <span className="flex items-center gap-1"><Clock size={12} /> {op.submitted_at || op.created_at}</span>
      </div>

      {Array.isArray(pv.fields) && pv.fields.length > 0 && (
        <div className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-stone-50 p-2">
          {pv.fields.map((f, i) => (
            <div key={i} className="flex justify-between gap-2 text-xs">
              <span className="text-ink-mute">{f.label}</span>
              <span className="font-semibold text-ink">{String(f.value)}</span>
            </div>
          ))}
        </div>
      )}

      {Array.isArray(pv.warnings) && pv.warnings.map((w, i) => (
        <p key={i} className="mb-1 flex items-center gap-1 text-[11px] text-warn-text"><AlertTriangle size={12} /> {w}</p>
      ))}

      {op.disposition && (
        <p className="mb-1 text-[11px] text-ink-mute">Disposisi: <strong>{op.disposition}</strong>{op.disposition_note ? ` — ${op.disposition_note}` : ""}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => onAct(op, "confirm")} loading={busy === op.id} data-testid={`ca-confirm-${op.id}`}>
          <CheckCircle2 size={14} /> Confirm &amp; Execute
        </Button>
        {!isWasteApproval && (
          <>
            <Button size="sm" variant="outline" onClick={() => onAct(op, "accept_variance")} disabled={busy === op.id} data-testid={`ca-accept-variance-${op.id}`}>
              <ShieldCheck size={14} /> Terima Variansi
            </Button>
            {!isFraud ? (
              <Button size="sm" variant="outline" onClick={() => onAct(op, "potential_fraud")} disabled={busy === op.id} data-testid={`ca-fraud-${op.id}`}>
                <ShieldAlert size={14} /> Potensi Fraud
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => onAct(op, "not_fraud")} disabled={busy === op.id} data-testid={`ca-not-fraud-${op.id}`}>
                <ShieldCheck size={14} /> Bukan Fraud
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onAct(op, "investigate")} disabled={busy === op.id} data-testid={`ca-investigate-${op.id}`}>
              <Search size={14} /> Investigasi
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={() => onAct(op, "reject")} disabled={busy === op.id} data-testid={`ca-reject-${op.id}`}>
          <XCircle size={14} /> Tolak
        </Button>
      </div>
    </Card>
  );
}

export default function ConfirmationAction() {
  const { user } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-assistant/confirmation-actions?status=pending");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat Confirmation Action");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (op, kind) => {
    setBusy(op.id);
    try {
      if (kind === "confirm") {
        const { data } = await api.post(`/ai-assistant/operations/${op.id}/confirm`);
        toast.success(data.status === "executed" ? "Dieksekusi" : `Status: ${data.status}`);
      } else if (kind === "reject") {
        await api.post(`/ai-assistant/operations/${op.id}/reject`);
        toast.success("Ditolak");
      } else {
        let note = "";
        if (kind === "potential_fraud" || kind === "investigate") {
          note = window.prompt(kind === "potential_fraud" ? "Catatan dugaan fraud (opsional):" : "Catatan investigasi (opsional):") || "";
        }
        const { data } = await api.post(`/ai-assistant/operations/${op.id}/disposition`, { disposition: kind, note });
        toast.success(data.status === "executed" ? "Variansi diterima & dieksekusi" : `Disposisi: ${kind}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aksi gagal");
    } finally {
      setBusy(null);
    }
  }, [toast, load]);

  if (user && !["OWNER", "SUPER_ADMIN"].includes(user.role)) return <Navigate to="/" replace />;

  return (
    <div data-testid="confirmation-action-page">
      <PageHeader
        title="Confirmation Action"
        subtitle="Aksi AI yang memerlukan perhatian Owner — penyesuaian stok & anomali."
        icon={ClipboardCheck}
      >
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ca-refresh">
          <RefreshCw size={14} /> Muat ulang
        </Button>
      </PageHeader>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Tidak ada aksi menunggu"
          subtitle="Semua aksi AI sudah diproses. Aksi operasional normal langsung dieksekusi oleh tim; hanya pengecualian & anomali yang muncul di sini."
        />
      ) : (
        <div className="space-y-3" data-testid="ca-list">
          {items.map((op) => <ActionCard key={op.id} op={op} busy={busy} onAct={act} />)}
        </div>
      )}
    </div>
  );
}
